import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { extractFunctionErrorMessage } from "@/lib/functionsError";
import { getAnalyticsIds } from "@/lib/analytics";
import { pushDataLayer } from "@/lib/dataLayer";

export interface OrderRequestItem {
  product_name: string;
  product_sku?: string;
  category?: string;
  quantity: number;
}

export interface ContactInfo {
  company: string;
  name: string;
  email: string;
  phone: string;
  message?: string;
}

export interface CompanyProfile {
  company_name: string;
  contact_name: string;
  phone: string;
}

interface OrderRequestContextValue {
  items: OrderRequestItem[];
  totalQty: number;
  add: (item: Omit<OrderRequestItem, "quantity">, qty?: number) => void;
  setQty: (sku: string | undefined, name: string, qty: number) => void;
  remove: (sku: string | undefined, name: string) => void;
  clear: () => void;
  getQty: (sku: string | undefined, name: string) => number;
  submit: (contact: ContactInfo) => Promise<{ ok: boolean; error?: string; request_id?: string }>;
  lookupProfile: (email: string) => Promise<CompanyProfile | null>;
}

const Ctx = createContext<OrderRequestContextValue | undefined>(undefined);

const STORAGE_KEY = "enzos_order_request_v1";

function keyOf(sku: string | undefined, name: string) {
  return `${sku || ""}::${name}`;
}

function loadItems(): OrderRequestItem[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p) => p && typeof p.product_name === "string")
      .map((p) => ({
        product_name: String(p.product_name),
        product_sku: p.product_sku ? String(p.product_sku) : undefined,
        category: p.category ? String(p.category) : undefined,
        quantity: Math.max(1, Number(p.quantity) || 1),
      }));
  } catch {
    return [];
  }
}

export function OrderRequestProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<OrderRequestItem[]>(loadItems);

  // Persist to localStorage so the request list survives navigation and reloads.
  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [items]);

  // Keep the list in sync across open tabs.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setItems(loadItems());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const add: OrderRequestContextValue["add"] = useCallback((item, qty = 1) => {
    setItems((prev) => {
      const k = keyOf(item.product_sku, item.product_name);
      const idx = prev.findIndex((p) => keyOf(p.product_sku, p.product_name) === k);
      if (idx === -1) return [...prev, { ...item, quantity: Math.max(1, qty) }];
      const copy = [...prev];
      copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
      return copy;
    });
  }, []);

  const setQty: OrderRequestContextValue["setQty"] = useCallback((sku, name, qty) => {
    setItems((prev) => {
      const k = keyOf(sku, name);
      if (qty <= 0) return prev.filter((p) => keyOf(p.product_sku, p.product_name) !== k);
      return prev.map((p) => (keyOf(p.product_sku, p.product_name) === k ? { ...p, quantity: qty } : p));
    });
  }, []);

  const remove: OrderRequestContextValue["remove"] = useCallback((sku, name) => {
    setItems((prev) => prev.filter((p) => keyOf(p.product_sku, p.product_name) !== keyOf(sku, name)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const getQty = useCallback(
    (sku: string | undefined, name: string) => {
      const found = items.find((p) => keyOf(p.product_sku, p.product_name) === keyOf(sku, name));
      return found?.quantity || 0;
    },
    [items],
  );

  const lookupProfile = useCallback(async (email: string): Promise<CompanyProfile | null> => {
    const clean = email.trim().toLowerCase();
    if (!clean) return null;
    try {
      const { data, error } = await supabase.functions.invoke("lookup-company-profile", { body: { email: clean } });
      if (error || !data?.ok || !data.found || !data.profile) return null;
      return {
        company_name: data.profile.company_name || "",
        contact_name: data.profile.contact_name || "",
        phone: data.profile.phone || "",
      };
    } catch {
      return null;
    }
  }, []);

  const submit = useCallback(
    async (contact: ContactInfo) => {
      if (items.length === 0) return { ok: false, error: "Your request list is empty." };
      if (!contact.company?.trim()) return { ok: false, error: "Please enter your company name." };
      if (!contact.name?.trim()) return { ok: false, error: "Please enter your contact name." };
      if (!contact.phone?.trim()) return { ok: false, error: "Please enter your phone number." };
      if (!contact.email?.trim()) return { ok: false, error: "Please enter your email address." };

      try {
        const { data, error } = await supabase.functions.invoke("submit-order-request", {
          body: {
            items,
            contact,
            page_path: typeof window !== "undefined" ? window.location.pathname : "",
            page_url: typeof window !== "undefined" ? window.location.href : "",
            page_name: typeof document !== "undefined" ? document.title : "",
            referrer: typeof document !== "undefined" ? document.referrer : "",
            // Links the request to the visitor's journey; the server records the conversion.
            analytics: getAnalyticsIds(),
          },
        });

        if (error) {
          const detail = await extractFunctionErrorMessage(error);
          return { ok: false, error: detail || "Network error. Please try again." };
        }
        if (!data || data.ok === false) {
          return { ok: false, error: data?.error || "Submission failed. Please try again." };
        }
        pushDataLayer("quote_request", { item_count: items.length, total_quantity: items.reduce((s, i) => s + i.quantity, 0) });
        setItems([]);
        return { ok: true, request_id: data.request_id };
      } catch (e) {
        return { ok: false, error: "Unexpected error. Please try again or call us." };
      }
    },
    [items],
  );

  const totalQty = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, totalQty, add, setQty, remove, clear, getQty, submit, lookupProfile }),
    [items, totalQty, add, setQty, remove, clear, getQty, submit, lookupProfile],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOrderRequest() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useOrderRequest must be used within OrderRequestProvider");
  return v;
}
